import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import * as XLSX from 'xlsx'
import apiClient from '../../config/api'

const ManageProducts = () => {
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [units, setUnits] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: ''
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [productsResponse, categoriesResponse, unitsResponse] = await Promise.all([
          apiClient.get('/api/products/products/'),
          apiClient.get('/api/products/categories/'),
          apiClient.get('/api/products/units/')
        ])
        
        setProducts(productsResponse.data)
        setFilteredProducts(productsResponse.data)
        setCategories(categoriesResponse.data || [])
        setUnits(unitsResponse.data || [])
      } catch (error) {
        console.error('Error fetching data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch data. Please try again later.'
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    let filtered = [...products]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower) ||
        product.sku?.toLowerCase().includes(searchLower) ||
        product.category_name?.toLowerCase().includes(searchLower)
      )
    }

    if (filters.category) {
      filtered = filtered.filter(product => product.category_name === filters.category)
    }

    if (filters.status) {
      const statusFilter = filters.status === 'active'
      filtered = filtered.filter(product => product.is_active === statusFilter)
    }

    setFilteredProducts(filtered)
  }, [products, filters])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleRowClick = (product, e) => {
    // Don't trigger if clicking on action buttons
    if (e.target.closest('button') || e.target.closest('.btn')) {
      return
    }
    
    const imageUrl = product.image_url || product.image || null
    const htmlContent = `
      <div style="text-align: left; font-size: 14px;">
        ${imageUrl ? `
          <div style="text-align: center; margin-bottom: 15px;">
            <img src="${imageUrl}" alt="${product.name}" style="max-width: 200px; max-height: 200px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; padding: 4px; background: white;">
          </div>
        ` : `
          <div style="text-align: center; margin-bottom: 15px; padding: 20px; background-color: #f8f9fa; border-radius: 4px; border: 1px dashed #dee2e6; color: #6c757d;">
            <i class="bi bi-image" style="font-size: 48px;"></i>
            <div style="margin-top: 10px; font-size: 12px;">No image available</div>
          </div>
        `}
        <p><strong>ID:</strong> ${product.id}</p>
        <p><strong>Name:</strong> ${product.name}</p>
        <p><strong>Description:</strong> ${product.description || 'N/A'}</p>
        <p><strong>SKU:</strong> ${product.sku}</p>
        <p><strong>Category:</strong> ${product.category_name || 'N/A'} ${product.category ? `(ID: ${product.category})` : ''}</p>
        <p><strong>Unit:</strong> ${product.unit_name || 'N/A'} ${product.unit ? `(ID: ${product.unit})` : ''}</p>
        <p><strong>Status:</strong> <span style="color: ${product.is_active ? '#28a745' : '#dc3545'}">${product.is_active ? 'Active' : 'Inactive'}</span></p>
        <p><strong>Created:</strong> ${formatDate(product.created_at)}</p>
        <p><strong>Updated:</strong> ${formatDate(product.updated_at)}</p>
      </div>
    `
    
    Swal.fire({
      title: 'Product Details',
      html: htmlContent,
      icon: 'info',
      width: '500px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6'
    })
  }

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/api/products/products/')
      setProducts(response.data)
      setFilteredProducts(response.data)
    } catch (error) {
      console.error('Error fetching products:', error)
    }
  }

  const escapeHtml = (text) => {
    if (!text) return ''
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }
    return text.replace(/[&<>"']/g, m => map[m])
  }

  const getExportRows = () => {
    if (!filteredProducts.length) {
      Swal.fire({
        icon: 'info',
        title: 'No data to export',
        text: 'Adjust filters to include at least one product.'
      })
      return null
    }

    return filteredProducts.map(product => ({
      Name: product.name || '',
      Description: product.description || '',
      SKU: product.sku || '',
      Category: product.category_name || '',
      Status: product.is_active ? 'Active' : 'Inactive'
    }))
  }

  const handleExportExcel = () => {
    const rows = getExportRows()
    if (!rows) return

    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products')

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([buffer], { type: 'application/octet-stream' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `manage-products-${new Date().toISOString().split('T')[0]}.xlsx`
    link.click()
    window.URL.revokeObjectURL(url)
  }

  const handleExportPdf = () => {
    const rows = getExportRows()
    if (!rows) return

    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text(' Products', 14, 16)
    doc.autoTable({
      startY: 22,
      head: [['Name', 'Description', 'SKU', 'Category', 'Status']],
      body: rows.map(row => [row.Name, row.Description, row.SKU, row.Category, row.Status]),
      styles: { fontSize: 9 }
    })
    doc.save(`manage-products-${new Date().toISOString().split('T')[0]}.pdf`)
  }

  const handleEditClick = async (product, e) => {
    e.stopPropagation() // Prevent row click
    
    const categoryOptions = categories.map(cat => 
      `<option value="${cat.id}" ${product.category === cat.id ? 'selected' : ''}>${escapeHtml(cat.name)}</option>`
    ).join('')
    
    const unitOptions = units.map(unit => {
      const displayName = unit.abbreviation 
        ? `${escapeHtml(unit.name)} (${escapeHtml(unit.abbreviation)})` 
        : escapeHtml(unit.name)
      return `<option value="${unit.id}" ${product.unit === unit.id ? 'selected' : ''}>${displayName}</option>`
    }).join('')

    const escapedSku = escapeHtml(product.sku || '')
    const escapedName = escapeHtml(product.name || '')
    const currentImageUrl = product.image_url || product.image || null

    const { value: formValues } = await Swal.fire({
      title: '<span style="color: #333; font-size: 24px;">✏️ Edit Product</span>',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">SKU <span style="color: red;">*</span></label>
          <input id="swal-sku" class="swal2-input" value="${escapedSku}" placeholder="Enter SKU" style="margin-top: 0;">
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Product Name <span style="color: red;">*</span></label>
          <input id="swal-name" class="swal2-input" value="${escapedName}" placeholder="Enter Product Name" style="margin-top: 0;">
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Description</label>
          <textarea id="swal-description" class="swal2-textarea" placeholder="Enter Description" style="margin-top: 0; min-height: 80px;">${escapeHtml(product.description || '')}</textarea>
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Category</label>
          <select id="swal-category" class="swal2-select" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; margin-top: 0; background-color: white;">
            <option value="">Select Category</option>
            ${categoryOptions}
          </select>
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Unit</label>
          <select id="swal-unit" class="swal2-select" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; margin-top: 0; background-color: white;">
            <option value="">Select Unit</option>
            ${unitOptions}
          </select>
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Product Image</label>
          <input type="file" id="swal-image" accept="image/*" class="swal2-input" style="margin-top: 0; padding: 8px;">
          <small style="display: block; margin-top: 5px; color: #6c757d; font-size: 12px;">Accepted formats: JPG, PNG, GIF. Max size: 5MB</small>
          ${currentImageUrl ? `
            <div style="margin-top: 10px; padding: 10px; background-color: #f8f9fa; border-radius: 4px; border: 1px solid #e9ecef;">
              <div style="font-size: 12px; color: #6c757d; margin-bottom: 8px;">Current Image:</div>
              <img src="${currentImageUrl}" alt="Current product image" style="max-width: 200px; max-height: 200px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; padding: 4px; background: white;">
            </div>
          ` : `
            <div style="margin-top: 10px; padding: 10px; background-color: #fff3cd; border-radius: 4px; border: 1px solid #ffc107; font-size: 12px; color: #856404;">
              <i class="bi bi-info-circle"></i> No image currently set for this product.
            </div>
          `}
          
          <div style="margin-top: 20px; padding: 12px; background-color: #f8f9fa; border-radius: 4px; border: 1px solid #e9ecef;">
            <label style="display: flex; align-items: center; font-weight: 600; color: #555; font-size: 14px; cursor: pointer;">
              <input type="checkbox" id="swal-is-active" ${product.is_active ? 'checked' : ''} style="margin-right: 10px; width: 18px; height: 18px; cursor: pointer;">
              Active Status
            </label>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: '<i class="bi bi-check-circle"></i> Update Product',
      confirmButtonColor: '#3085d6',
      cancelButtonText: '<i class="bi bi-x-circle"></i> Cancel',
      cancelButtonColor: '#6c757d',
      width: '600px',
      customClass: {
        popup: 'swal2-popup-custom'
      },
      preConfirm: () => {
        const sku = document.getElementById('swal-sku').value.trim()
        const name = document.getElementById('swal-name').value.trim()
        const description = document.getElementById('swal-description').value.trim()
        const category = document.getElementById('swal-category').value
        const unit = document.getElementById('swal-unit').value
        const isActive = document.getElementById('swal-is-active').checked
        const imageInput = document.getElementById('swal-image')
        const imageFile = imageInput.files[0]

        if (!sku) {
          Swal.showValidationMessage('SKU is required')
          return false
        }
        if (!name) {
          Swal.showValidationMessage('Product name is required')
          return false
        }

        // Validate image if provided
        if (imageFile) {
          if (!imageFile.type.startsWith('image/')) {
            Swal.showValidationMessage('Please select an image file.')
            return false
          }
          if (imageFile.size > 5 * 1024 * 1024) {
            Swal.showValidationMessage('Image size should be less than 5MB.')
            return false
          }
        }

        return {
          sku,
          name,
          description,
          category: category ? parseInt(category) : null,
          unit: unit ? parseInt(unit) : null,
          is_active: isActive,
          image: imageFile || null
        }
      }
    })

    if (formValues) {
      try {
        // Use FormData if image is present, otherwise use JSON
        const hasImage = formValues.image !== null
        let payload
        let config = {}
        
        if (hasImage) {
          // Use FormData for file upload
          payload = new FormData()
          payload.append('sku', formValues.sku)
          payload.append('name', formValues.name)
          payload.append('description', formValues.description)
          // Convert boolean to string for FormData
          payload.append('is_active', formValues.is_active ? 'true' : 'false')
          
          // Only append category and unit if they have valid integer values
          if (formValues.category !== null && formValues.category !== '') {
            payload.append('category', formValues.category)
          }
          if (formValues.unit !== null && formValues.unit !== '') {
            payload.append('unit', formValues.unit)
          }
          
          // Append image file
          payload.append('image', formValues.image)
          
          // Don't set Content-Type manually - let browser set it with boundary
          // axios will automatically set the correct Content-Type with boundary
        } else {
          // Use JSON payload when no image
          payload = {
            sku: formValues.sku,
            name: formValues.name,
            description: formValues.description,
            is_active: formValues.is_active
          }
          
          // Only include category and unit if they have valid integer values
          if (formValues.category !== null && formValues.category !== '') {
            payload.category = formValues.category
          }
          if (formValues.unit !== null && formValues.unit !== '') {
            payload.unit = formValues.unit
          }
        }
        
        await apiClient.put(`/api/products/products/${product.id}/`, payload, config)
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Product updated successfully.',
          confirmButtonColor: '#3085d6',
          timer: 2000
        })
        await fetchProducts()
      } catch (error) {
        console.error('Error updating product:', error)
        
        // Extract error message from response - simple approach
        let errorMessage = 'Failed to update product. Please try again.'
        
        if (error.response) {
          const errorData = error.response.data
          
          // Handle string responses
          if (typeof errorData === 'string') {
            errorMessage = errorData
          } else if (errorData && typeof errorData === 'object') {
            // Try to get the error message in order of priority
            if (errorData.message) {
              errorMessage = Array.isArray(errorData.message) ? errorData.message[0] : String(errorData.message)
            } else if (errorData.detail) {
              errorMessage = Array.isArray(errorData.detail) ? errorData.detail[0] : String(errorData.detail)
            } else if (errorData.error) {
              errorMessage = Array.isArray(errorData.error) ? errorData.error[0] : String(errorData.error)
            } else if (errorData.non_field_errors) {
              errorMessage = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : String(errorData.non_field_errors)
            } else {
              // Get first field error found
              const firstErrorKey = Object.keys(errorData)[0]
              if (firstErrorKey) {
                const firstError = errorData[firstErrorKey]
                errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError)
              } else {
                // No specific error message, use status code info
                const status = error.response.status
                if (status === 500) {
                  errorMessage = 'Server error (500). Please contact support or try again later.'
                } else if (status === 400) {
                  errorMessage = 'Invalid request. Please check your input and try again.'
                } else {
                  errorMessage = `Request failed with status code ${status}`
                }
              }
            }
          } else {
            // No data in response, use status code
            const status = error.response.status
            if (status === 500) {
              errorMessage = 'Server error (500). Please contact support or try again later.'
            } else {
              errorMessage = `Request failed with status code ${status}`
            }
          }
        } else if (error.message) {
          // Use Axios error message
          errorMessage = String(error.message)
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection and try again.'
        }
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonColor: '#d33'
        })
      }
    }
  }

  const handleDelete = async (productId, e) => {
    e.stopPropagation() // Prevent row click
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    })

    if (result.isConfirmed) {
      try {
        await apiClient.delete(`/api/products/products/${productId}/`)
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Product has been deleted.',
          confirmButtonColor: '#3085d6',
          timer: 2000
        })
        await fetchProducts()
      } catch (error) {
        console.error('Error deleting product:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || error.response?.data?.message || 'Failed to delete product. Please try again.',
          confirmButtonColor: '#d33'
        })
      }
    }
  }

  const handleNavigate = (screen) => {
    // Dispatch custom event for navigation
    const event = new CustomEvent('navigateToScreen', { detail: { screen } })
    window.dispatchEvent(event)
  }

  const totalProducts = filteredProducts.length
  const activeProducts = filteredProducts.filter(p => p.is_active).length
  const inactiveProducts = filteredProducts.filter(p => !p.is_active).length

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Products</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Manage Products</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {/* Header with Stats and Unit Management */}
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ 
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-box-seam me-2"></i>
                      Manage Products
                    </h5>
                    
                    {/* Stats Badges */}
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="d-flex align-items-center" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                      }}>
                        <i className="bi bi-box-seam me-2"></i>
                        <span>Total: <strong>{totalProducts}</strong></span>
                      </div>
                      
                      <div className="d-flex align-items-center" style={{
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)'
                      }}>
                        <i className="bi bi-check-circle me-2"></i>
                        <span>Active: <strong>{activeProducts}</strong></span>
                      </div>
                      
                      <div className="d-flex align-items-center" style={{
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)'
                      }}>
                        <i className="bi bi-x-circle me-2"></i>
                        <span>Inactive: <strong>{inactiveProducts}</strong></span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Unit Management Buttons */}
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleNavigate('add-unit')}
                      style={{
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500',
                        boxShadow: '0 2px 6px rgba(13, 110, 253, 0.25)'
                      }}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Add Unit
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handleNavigate('manage-units')}
                      style={{
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500',
                        borderWidth: '2px'
                      }}
                    >
                      <i className="bi bi-gear me-1"></i>
                      Manage Unit
                    </button>
                  </div>
                </div>

                {/* Filters */}
                <div className="row mb-3 align-items-center">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search products..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Category"
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="col-md-2 d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                      onClick={handleExportPdf}
                    >
                      <i className="bi bi-file-earmark-pdf me-1"></i>
                      PDF
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm d-flex align-items-center"
                      onClick={handleExportExcel}
                    >
                      <i className="bi bi-file-earmark-excel me-1"></i>
                      Excel
                    </button>
                  </div>
                </div>

                {/* Products Table */}
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: '80px' }}>Image</th>
                          <th>Name</th>
                          <th>Description</th>
                          <th>SKU</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center">No products found</td>
                          </tr>
                        ) : (
                          filteredProducts.map(product => {
                            const imageUrl = product.image_url || product.image || null
                            return (
                              <tr 
                                key={product.id}
                                onClick={(e) => handleRowClick(product, e)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td onClick={(e) => e.stopPropagation()}>
                                  {imageUrl ? (
                                    <img 
                                      src={imageUrl} 
                                      alt={product.name}
                                      style={{
                                        width: '50px',
                                        height: '50px',
                                        objectFit: 'contain',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '2px',
                                        backgroundColor: 'white'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none'
                                        const placeholder = e.target.parentElement.querySelector('.image-placeholder')
                                        if (placeholder) placeholder.style.display = 'flex'
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className="image-placeholder"
                                    style={{
                                      display: imageUrl ? 'none' : 'flex',
                                      width: '50px',
                                      height: '50px',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '4px',
                                      color: '#6c757d',
                                      fontSize: '20px'
                                    }}
                                  >
                                    <i className="bi bi-image"></i>
                                  </div>
                                </td>
                                <td>{product.name}</td>
                                <td>{product.description || 'N/A'}</td>
                                <td>{product.sku}</td>
                                <td>{product.category_name || 'N/A'}</td>
                                <td>
                                  <span className={`badge ${product.is_active ? 'bg-success' : 'bg-danger'}`}>
                                    {product.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <button
                                    className="btn btn-sm btn-primary me-2"
                                    onClick={(e) => handleEditClick(product, e)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={(e) => handleDelete(product.id, e)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageProducts

